import { mapState } from 'vuex';
import TimeInput from './TimeInput.vue';
export default {
  name: 'BridgeMonitoringRuleDialog',
  components: {
    TimeInput
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    rule: {
      type: Object,
      default: null
    },
    bridgeId: {
      type: Number,
      required: true
    },
    monitoringConfigId: {
      type: Number,
      required: true
    },
    monitoringConfig: {
      type: Object,
      required: true
    },
    tenantId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      ruleData: {
        name: '',
        ruleType: 'packet_threshold',
        enabled: true,
        conditions: {},
        properties: {
          severity: 'warning',
          autoResolve: true,
          cooldownPeriod: 300,
          notificationChannels: ['alarmLog']
        }
      },
      ruleTypes: [{
        text: this.$i18n_t('Connectors.Monitoring.rules.types.packet_threshold'),
        value: 'packet_threshold'
      }, {
        text: this.$i18n_t('Connectors.Monitoring.rules.types.state_duration'),
        value: 'state_duration'
      }, {
        text: this.$i18n_t('Connectors.Monitoring.rules.types.no_data'),
        value: 'no_data'
      }, {
        text: this.$i18n_t('Connectors.Monitoring.rules.types.state_count'),
        value: 'state_count'
      }, {
        text: this.$i18n_t('Connectors.Monitoring.rules.types.state_change'),
        value: 'state_change'
      }],
      operatorOptions: [{
        text: this.$i18n_t('Connectors.Monitoring.rules.operators.greater_than'),
        value: 'greater_than'
      }, {
        text: this.$i18n_t('Connectors.Monitoring.rules.operators.greater_or_equal_than'),
        value: 'greater_or_equal_than'
      }, {
        text: this.$i18n_t('Connectors.Monitoring.rules.operators.less_than'),
        value: 'less_than'
      }, {
        text: this.$i18n_t('Connectors.Monitoring.rules.operators.less_or_equal_than'),
        value: 'less_or_equal_than'
      }, {
        text: this.$i18n_t('Connectors.Monitoring.rules.operators.equal'),
        value: 'equal'
      }, {
        text: this.$i18n_t('Connectors.Monitoring.rules.operators.not_equal'),
        value: 'not_equal'
      }],
      aggregationOptions: [{
        text: this.$i18n_t('Connectors.Monitoring.rules.aggregations.none'),
        value: 'none'
      }, {
        text: this.$i18n_t('Connectors.Monitoring.rules.aggregations.sum'),
        value: 'sum'
      }, {
        text: this.$i18n_t('Connectors.Monitoring.rules.aggregations.avg'),
        value: 'avg'
      }, {
        text: this.$i18n_t('Connectors.Monitoring.rules.aggregations.max'),
        value: 'max'
      }, {
        text: this.$i18n_t('Connectors.Monitoring.rules.aggregations.min'),
        value: 'min'
      }],
      stateOptions: [{
        text: this.$i18n_t('Connectors.Monitoring.rules.states.error'),
        value: 1
      }, {
        text: this.$i18n_t('Connectors.Monitoring.rules.states.unknown'),
        value: 2
      }, {
        text: this.$i18n_t('Connectors.Monitoring.rules.states.disabled'),
        value: 3
      }, {
        text: this.$i18n_t('Connectors.Monitoring.rules.states.unassigning'),
        value: 4
      }, {
        text: this.$i18n_t('Connectors.Monitoring.rules.states.assigning'),
        value: 5
      }, {
        text: this.$i18n_t('Connectors.Monitoring.rules.states.waiting'),
        value: 6
      }, {
        text: this.$i18n_t('Connectors.Monitoring.rules.states.active'),
        value: 7
      }],
      severityOptions: [{
        text: this.$i18n_t('Connectors.Monitoring.rules.severities.warning'),
        value: 'warning'
      }, {
        text: this.$i18n_t('Connectors.Monitoring.rules.severities.error'),
        value: 'error'
      }]
    };
  },
  computed: {
    ...mapState('app', ['color', 'inpOutlined', 'inpFilled']),
    internalOpen: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },
    stateOptionsWithAny() {
      return [{
        text: this.$i18n_t('Connectors.Monitoring.rules.states.any'),
        value: 0
      }, ...this.stateOptions];
    },
    hasEmailConnector() {
      var _this$monitoringConfi;
      return !!((_this$monitoringConfi = this.monitoringConfig) !== null && _this$monitoringConfi !== void 0 && (_this$monitoringConfi = _this$monitoringConfi.properties) !== null && _this$monitoringConfi !== void 0 && _this$monitoringConfi.emailConnectorId);
    },
    hasWebhookConnector() {
      var _this$monitoringConfi2;
      return !!((_this$monitoringConfi2 = this.monitoringConfig) !== null && _this$monitoringConfi2 !== void 0 && (_this$monitoringConfi2 = _this$monitoringConfi2.properties) !== null && _this$monitoringConfi2 !== void 0 && _this$monitoringConfi2.webhookConnectorId);
    },
    emailLabel() {
      return this.hasEmailConnector ? this.$i18n_t('Connectors.Monitoring.rules.fields.sendEmail') : `${this.$i18n_t('Connectors.Monitoring.rules.fields.sendEmail')} (${this.$i18n_t('Connectors.Monitoring.rules.notConfigured')})`;
    },
    webhookLabel() {
      return this.hasWebhookConnector ? this.$i18n_t('Connectors.Monitoring.rules.fields.sendWebhook') : `${this.$i18n_t('Connectors.Monitoring.rules.fields.sendWebhook')} (${this.$i18n_t('Connectors.Monitoring.rules.notConfigured')})`;
    },
    isValid() {
      if (!this.ruleData.name || !this.ruleData.ruleType) {
        return false;
      }
      const {
        conditions
      } = this.ruleData;

      // validate based on rule type
      switch (this.ruleData.ruleType) {
        case 'packet_threshold':
          return conditions.operator && conditions.threshold != null && conditions.timeWindow;
        case 'state_duration':
          return conditions.targetState && conditions.operator && conditions.threshold && conditions.evaluationWindow;
        case 'no_data':
          return conditions.threshold != null;
        case 'state_count':
          return conditions.targetState && conditions.operator && conditions.threshold && conditions.timeWindow;
        case 'state_change':
          return conditions.targetState != null && conditions.operator && conditions.threshold && conditions.timeWindow;
        default:
          return false;
      }
    },
    conditionSummary() {
      var _this$operatorOptions;
      const {
        conditions
      } = this.ruleData;

      // show summary as soon as we have basic data (even if not all fields are valid)
      if (!this.ruleData.ruleType) {
        return '';
      }
      const t = key => this.$i18n_t(`Connectors.Monitoring.rules.summaries.${key}`);
      const operatorText = ((_this$operatorOptions = this.operatorOptions.find(op => op.value === conditions.operator)) === null || _this$operatorOptions === void 0 ? void 0 : _this$operatorOptions.text) || '';
      const getStateName = stateValue => {
        var _this$stateOptions$fi;
        if (stateValue === 0) {
          return this.$i18n_t('Connectors.Monitoring.rules.states.any');
        }
        return ((_this$stateOptions$fi = this.stateOptions.find(s => s.value === stateValue)) === null || _this$stateOptions$fi === void 0 ? void 0 : _this$stateOptions$fi.text) || '';
      };

      // check if required fields exist for this rule type
      const hasRequiredFields = () => {
        switch (this.ruleData.ruleType) {
          case 'packet_threshold':
            return conditions.operator && conditions.threshold != null && conditions.timeWindow;
          case 'state_duration':
            return conditions.targetState && conditions.operator && conditions.threshold && conditions.evaluationWindow;
          case 'no_data':
            return conditions.threshold != null;
          case 'state_count':
            return conditions.targetState && conditions.operator && conditions.threshold && conditions.timeWindow;
          case 'state_change':
            return conditions.targetState != null && conditions.operator && conditions.threshold && conditions.timeWindow;
          default:
            return false;
        }
      };

      // only show summary if required fields are present
      if (!hasRequiredFields()) {
        return '';
      }
      switch (this.ruleData.ruleType) {
        case 'packet_threshold':
          return `${t('triggerWhen')} ${t('bridgeReceives')} ${operatorText.toLowerCase()} ` + `${conditions.threshold} ${t('packetsWithin')} ${conditions.timeWindow} ${t('seconds')} ` + `${t('receives') || ''}.`;
        case 'state_duration':
          return `${t('triggerWhen')} ${t('bridgeStaysIn')} "${getStateName(conditions.targetState)}" ` + `${operatorText.toLowerCase()} ${conditions.threshold} ${t('seconds')} ` + `(${t('evaluatedOver')} ${conditions.evaluationWindow} ${t('seconds')}) ${t('remains') || ''}.`;
        case 'no_data':
          const activeNote = conditions.requireActiveState ? ` (${t('onlyWhenActive')})` : '';
          return `${t('triggerWhen')} ${t('noPacketsFor')} ${conditions.threshold} ${t('seconds')}${activeNote}.`;
        case 'state_count':
          return `${t('triggerWhen')} "${getStateName(conditions.targetState)}" ${t('stateOccurs')} ` + `${operatorText.toLowerCase()} ${conditions.threshold} ${t('timesWithin')} ` + `${conditions.timeWindow} ${t('seconds')} ${t('occurs') || ''}.`;
        case 'state_change':
          const stateChangeTarget = conditions.targetState === 0 ? t('anyState') : `"${getStateName(conditions.targetState)}"`;
          return `${t('triggerWhen')} ${t('bridgeChangesTo')} ${stateChangeTarget} ` + `${operatorText.toLowerCase()} ${conditions.threshold} ${t('timesWithin')} ` + `${conditions.timeWindow} ${t('seconds')} (${t('flappingDetection')}) ${t('changes') || ''}.`;
        default:
          return '';
      }
    },
    behaviorSummary() {
      const {
        properties
      } = this.ruleData;
      const t = key => this.$i18n_t(`Connectors.Monitoring.rules.summaries.${key}`);
      const severityText = properties.severity === 'error' ? 'ERROR' : 'WARNING';
      const cooldownMin = Math.floor(properties.cooldownPeriod / 60);
      const cooldownSec = properties.cooldownPeriod % 60;
      const cooldownText = cooldownMin > 0 ? `${cooldownMin} ${cooldownMin > 1 ? t('minutes') : t('minute')}${cooldownSec > 0 ? ` ${t('and')} ${cooldownSec} ${t('seconds')}` : ''}` : `${cooldownSec} ${t('seconds')}`;
      let summary = `${t('whenTriggered')} ${severityText} ${t('alert')}`;
      if (properties.autoResolve) {
        summary += ` ${t('and')} ${t('autoResolve')}`;
      }
      summary += `. ${t('afterResolution')} ${cooldownText} ${t('beforeTriggerAgain')}.`;
      return summary;
    }
  },
  watch: {
    value(isOpen) {
      // reinitialize form when dialog opens
      if (isOpen) {
        if (this.rule) {
          this.ruleData = {
            ...this.rule,
            properties: {
              severity: 'warning',
              autoResolve: true,
              cooldownPeriod: 300,
              notificationChannels: ['alarmLog'],
              ...this.rule.properties
            }
          };
        } else {
          this.resetForm();
        }
      }
    },
    rule: {
      immediate: true,
      handler(newRule) {
        if (newRule) {
          this.ruleData = {
            ...newRule,
            properties: {
              severity: 'warning',
              autoResolve: true,
              cooldownPeriod: 300,
              notificationChannels: ['alarmLog'],
              ...newRule.properties
            }
          };
        } else {
          this.resetForm();
        }
      }
    }
  },
  methods: {
    onRuleTypeChange() {
      // reset conditions when rule type changes
      this.ruleData.conditions = this.getDefaultConditions(this.ruleData.ruleType);
    },
    getDefaultConditions(ruleType) {
      switch (ruleType) {
        case 'packet_threshold':
          return {
            operator: 'less_than',
            threshold: 10,
            timeWindow: 300,
            aggregation: 'none'
          };
        case 'state_duration':
          return {
            targetState: 1,
            // error
            operator: 'greater_than',
            threshold: 300,
            evaluationWindow: 600
          };
        case 'no_data':
          return {
            threshold: 600,
            requireActiveState: true
          };
        case 'state_count':
          return {
            targetState: 1,
            // error
            operator: 'greater_than',
            threshold: 5,
            timeWindow: 3600
          };
        case 'state_change':
          return {
            targetState: 0,
            // any
            operator: 'greater_than',
            threshold: 10,
            timeWindow: 300
          };
        default:
          return {};
      }
    },
    resetForm() {
      this.ruleData = {
        name: '',
        ruleType: 'packet_threshold',
        enabled: true,
        conditions: this.getDefaultConditions('packet_threshold'),
        properties: {
          severity: 'warning',
          autoResolve: true,
          cooldownPeriod: 300,
          notificationChannels: ['alarmLog']
        }
      };
    },
    save() {
      const ruleToSave = {
        ...this.ruleData,
        bridgeId: this.bridgeId,
        monitoringConfigId: this.monitoringConfigId,
        tenantId: this.tenantId
      };
      this.$emit('save', ruleToSave);
    },
    cancel() {
      this.resetForm();
      this.internalOpen = false;
    }
  }
};